import React, { useEffect, useState } from 'react'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InfoOutlined as InfoOutlinedIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { GenericTableV2, createColumns } from '../../../components/_EOS/GenericTable'
import { getCourierServiceSchedule } from '../config/api'
import {
  CourierCollectionPlannerFormDetailProps,
  CourierServiceScheduleDetailDto,
  CUT_OFF_MINUTES_MIN,
  CUT_OFF_MINUTES_MAX,
  ExceptionScheduleDto,
  isCutOffPreviousDay,
  PickupTimeDto,
  WEEK_DAYS,
  WeeklyScheduleDto
} from '../config/config'
import { formStyles } from './styles'

interface WeeklyScheduleView {
  dayId: number
  active: boolean
  pickupTimes: PickupTimeDto[]
}

const mapWeeklySchedulesFromApi = (
  weeklySchedules: WeeklyScheduleDto[]
): WeeklyScheduleView[] =>
  WEEK_DAYS.map((day) => {
    const apiDay = weeklySchedules.find((schedule) => schedule.dayId === day.dayId)
    return {
      dayId: day.dayId,
      active: apiDay?.active ?? false,
      pickupTimes: apiDay?.pickupTimes ?? []
    }
  })

const isCutOffMinutesValid = (minutes: number) =>
  minutes >= CUT_OFF_MINUTES_MIN && minutes <= CUT_OFF_MINUTES_MAX

const calculateCutOffTime = (pickupTime: string, cutOffMinutes: number): string | null => {
  if (!pickupTime || !isCutOffMinutesValid(cutOffMinutes)) return null

  const [hours, minutes] = pickupTime.split(':').map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null

  const totalMinutes = ((hours * 60 + minutes - cutOffMinutes) % CUT_OFF_MINUTES_MAX + CUT_OFF_MINUTES_MAX) % CUT_OFF_MINUTES_MAX
  const cutOffHours = Math.floor(totalMinutes / 60)
  const cutOffMins = totalMinutes % 60

  return `${String(cutOffHours).padStart(2, '0')}:${String(cutOffMins).padStart(2, '0')}`
}

const formatExceptionDate = (dateStr: string | null) => {
  if (!dateStr) return ''
  const date = new Date(`${dateStr.slice(0, 10)}T00:00:00`)
  return new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date)
}

export const CourierCollectionPlannerFormDetail: React.FC<CourierCollectionPlannerFormDetailProps> = ({
  courier
}) => {
  const { state: scheduleState, callApi: callScheduleApi } =
    useApi<CourierServiceScheduleDetailDto>()

  const [activeTab, setActiveTab] = useState<'weekly' | 'exceptions'>('weekly')
  const [weeklySchedules, setWeeklySchedules] = useState<WeeklyScheduleView[]>([])
  const [exceptions, setExceptions] = useState<ExceptionScheduleDto[]>([])
  const [expandedDays, setExpandedDays] = useState<Record<number, boolean>>(
    () => Object.fromEntries(WEEK_DAYS.map((day) => [day.dayId, true]))
  )

  const toggleDayExpanded = (dayId: number) => {
    setExpandedDays((prev) => ({ ...prev, [dayId]: !prev[dayId] }))
  }

  useEffect(() => {
    callScheduleApi(getCourierServiceSchedule(courier.courierServiceId))
      .then((response: CourierServiceScheduleDetailDto) => {
        const mappedSchedules = mapWeeklySchedulesFromApi(response.weeklySchedules)
        setWeeklySchedules(mappedSchedules)
        setExceptions(response.exceptions)
        setExpandedDays(
          Object.fromEntries(
            mappedSchedules.map((day) => [day.dayId, day.pickupTimes.length > 0])
          )
        )
      })
      .catch((error) => {
        console.log(error)
      })
  }, [courier.courierServiceId])

  const renderPickupTimesTable = (pickupTimes: PickupTimeDto[], emptyMessage: string) => (
    <GenericTableV2<PickupTimeDto>
      data={pickupTimes}
      keyField='courierServicePickupScheduleId'
      noResultsComponent={
        <Box sx={formStyles.emptyPickupTimes}>
          <InfoOutlinedIcon fontSize='small' sx={formStyles.emptyPickupTimesIcon} />
          <Typography variant='caption' color='textSecondary' sx={formStyles.emptyPickupTimesText}>
            {emptyMessage}
          </Typography>
        </Box>
      }
      columns={createColumns<PickupTimeDto>([
        {
          key: 'courierServicePickupScheduleId',
          title: 'Recolección',
          align: 'center',
          headerTooltip: 'Número correlativo de la recolección configurada',
          render: (_value, _row, index) => `#${index + 1}`
        },
        {
          key: 'pickupTime',
          title: 'Hora de Recolección',
          headerTooltip: 'Hora especifica para el retiro de paquetes.',
          render: (value) => (
            <TextField
              type='time'
              size='small'
              variant='outlined'
              value={(value as string).slice(0, 5)}
              disabled
            />
          )
        },
        {
          key: 'cutOffMinutes',
          title: 'Tiempo de Corte',
          headerTooltip: 'Tiempo antes de la recolección en los que se cierra ese envio de pedidos.',
          render: (value, row) => {
            const cutOffMinutes = value as number
            const cutOffTime = calculateCutOffTime(row.pickupTime.slice(0, 5), cutOffMinutes)
            return (
              <Box sx={formStyles.cutOffCell}>
                <TextField
                  type='number'
                  size='small'
                  variant='outlined'
                  value={cutOffMinutes}
                  disabled
                />
                <Typography variant='caption' color='secondary' sx={formStyles.cutOffCaption}>
                  {cutOffTime
                    ? `Se aceptan pedidos hasta las: ${cutOffTime}${isCutOffPreviousDay(row.pickupTime.slice(0, 5), cutOffMinutes) ? ' del día anterior' : ''}`
                    : 'No se pudo calcular el tiempo de corte.'}
                </Typography>
              </Box>
            )
          }
        }
      ])}
    />
  )

  if (scheduleState.status === Status.Loading) {
    return <Loading />
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          select
          fullWidth
          variant='outlined'
          label='Nombre del Courier'
          value={courier.courierName}
          disabled
        >
          <MenuItem value={courier.courierName}>{courier.courierName}</MenuItem>
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          variant='outlined'
          label='Nombre del Servicio'
          value={courier.name}
          disabled
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          type='number'
          variant='outlined'
          label='Capacidad Máxima de Unidades'
          placeholder='Ej: 500'
          value={courier.maxUnitsCapacity ?? ''}
          disabled
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          type='number'
          variant='outlined'
          label='Capacidad Máxima de Paquetes'
          placeholder='Ej: 200'
          value={courier.maxPackagesCapacity ?? ''}
          disabled
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, value) => setActiveTab(value)}
          sx={formStyles.tabs}
        >
          <Tab label='Horarios Semanales' value='weekly' />
          <Tab label='Excepciones de Recolección' value='exceptions' />
        </Tabs>
      </Grid>

      {activeTab === 'weekly' && (
        <Grid size={{ xs: 12 }}>
          {weeklySchedules.map((day) => {
            const dayName = WEEK_DAYS.find((d) => d.dayId === day.dayId)?.name
            return (
              <Box
                key={day.dayId}
                sx={formStyles.dayCard}
              >
                <Box
                  onClick={() => toggleDayExpanded(day.dayId)}
                  sx={formStyles.dayCardHeader}
                >
                  <Box sx={formStyles.dayCardHeaderLeft}>
                    {expandedDays[day.dayId] ? (
                      <ExpandLessIcon fontSize='small' />
                    ) : (
                      <ExpandMoreIcon fontSize='small' />
                    )}
                    <FormControlLabel
                      sx={formStyles.dayCheckbox}
                      onClick={(e) => e.stopPropagation()}
                      control={<Checkbox checked={day.active} color='primary' disabled />}
                      label={dayName}
                    />
                  </Box>
                </Box>
                {expandedDays[day.dayId] && (
                  <Box sx={formStyles.dayCardBody}>
                    {renderPickupTimesTable(
                      day.pickupTimes,
                      'No hay horarios configurados para este día.'
                    )}
                  </Box>
                )}
              </Box>
            )
          })}
        </Grid>
      )}

      {activeTab === 'exceptions' && (
        <Grid size={{ xs: 12 }}>
          {exceptions.length === 0 && (
            <Typography variant='body2' color='textSecondary'>
              No hay excepciones de recolección registradas.
            </Typography>
          )}

          {exceptions.map((exception) => (
            <Box
              key={exception.courierServiceScheduleId}
              sx={formStyles.exceptionCard}
            >
              <Box sx={formStyles.exceptionHeader}>
                <Box sx={formStyles.exceptionHeaderLeft}>
                  <CalendarTodayIcon fontSize='small' color='action' />
                  <Box>
                    <Typography variant='subtitle2'>{exception.description}</Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {formatExceptionDate(exception.exceptionDate)}
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={<Checkbox checked={exception.active} color='primary' disabled />}
                  label='Activo'
                />
              </Box>

              {exception.active && (
                <Box sx={formStyles.exceptionSchedulesWrapper}>
                  <Typography variant='body2' sx={formStyles.exceptionSchedulesTitle}>
                    Horarios de recolección
                  </Typography>
                  {renderPickupTimesTable(
                    exception.pickupTimes,
                    'No hay horarios configurados para esta fecha.'
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Grid>
      )}
    </Grid>
  )
}
